import React, { useEffect, useState, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { History, GitCommit, ChevronRight, Eye, Loader2 } from "lucide-react";
import { useEditor } from "../EditorContext";

const PAGE_SIZE = 20;

export default function ComponentHistoryList({ componentId }) {
    const [history, setHistory] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loadingDetail, setLoadingDetail] = useState(null);
    const { setDiffVersion, diffVersion } = useEditor();
    const { t } = useTranslation();

    const { call: getHistory, isLoading } = useBluelagoon({
        method: "getComponentHistory",
    });
    const { call: getDetail } = useBluelagoon({
        method: "getComponentVersionDetail",
    });

    useEffect(() => {
        if (!componentId) return;
        setHistory([]);
        setPage(1);
        getHistory({ id: componentId, page: 1, limit: PAGE_SIZE }).then((res) => {
            if (res?.data) {
                setHistory(res.data);
                setTotalPages(res.totalPages || 1);
            }
        });
    }, [componentId, getHistory]);

    const loadMore = useCallback(() => {
        const nextPage = page + 1;
        getHistory({ id: componentId, page: nextPage, limit: PAGE_SIZE }).then((res) => {
            if (res?.data) {
                setHistory((prev) => [...prev, ...res.data]);
                setPage(nextPage);
                setTotalPages(res.totalPages || 1);
            }
        });
    }, [componentId, page, getHistory]);

    const handleVersionClick = useCallback((version) => {
        if (diffVersion?.id === version.id) {
            setDiffVersion(null);
            return;
        }
        setLoadingDetail(version.id);
        getDetail({ id: version.id }).then((res) => {
            setLoadingDetail(null);
            if (res?.data) setDiffVersion(res.data);
        });
    }, [diffVersion, setDiffVersion, getDetail]);

    if (isLoading && history.length === 0) {
        return (
            <div className="flex items-center justify-center p-4">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            </div>
        );
    }

    if (history.length === 0) {
        return (
            <div className="p-4 text-xs text-gray-400 text-center">
                {t("componentHistory.noHistory")}
            </div>
        );
    }

    return (
        <div className="flex flex-col border-t bg-gray-50/50">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 flex items-center gap-2 border-b">
                <History className="w-3 h-3" />
                {t("componentHistory.title")}
            </div>
            <div className="overflow-y-auto max-h-48">
                {history.map((version) => (
                    <button
                        key={version.id}
                        onClick={() => handleVersionClick(version)}
                        disabled={loadingDetail === version.id}
                        className={`w-full text-left px-3 py-2 text-xs border-b last:border-0 hover:bg-gray-100 flex items-center gap-2 group transition-colors ${
                            diffVersion?.id === version.id
                                ? "bg-blue-50 text-blue-700 border-l-2 border-l-blue-500"
                                : "text-gray-600"
                        }`}
                    >
                        <GitCommit className="w-3 h-3 flex-shrink-0 opacity-70" />
                        <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{version.version || "v1.0.0"}</div>
                            <div className="text-[10px] text-gray-400 truncate">
                                {format(new Date(version.createdAt), "MMM d, h:mm a")}
                            </div>
                        </div>
                        {loadingDetail === version.id ? (
                            <Loader2 className="w-3 h-3 animate-spin text-gray-400 flex-shrink-0" />
                        ) : diffVersion?.id === version.id ? (
                            <Eye className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        ) : (
                            <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400" />
                        )}
                    </button>
                ))}
                {page < totalPages && (
                    <button
                        onClick={loadMore}
                        disabled={isLoading}
                        className="w-full py-2 text-xs text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                        {isLoading ? t("common.loading") : t("componentHistory.loadMore")}
                    </button>
                )}
            </div>
        </div>
    );
}
